/**
 * Discovery offer product image — full-bleed cover in cards and detail hero.
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CatalogImagePlaceholder } from 'pi-kiosk-shared/ui';
import {
  isBrokenCatalogImageUrl,
  noteCatalogImageLoadFailure,
} from 'pi-kiosk-shared';
import type { PublicDiscoveryOfferItemDTO } from '../discovery/publicDiscoveryTypes.js';
import { resolveDiscoveryOfferDisplayImageUrl } from './browseOfferPresentation.js';

export type DiscoveryOfferAspectRatio = '4/3' | 'square';

const ASPECT_CLASS: Record<DiscoveryOfferAspectRatio, string> = {
  '4/3': 'aspect-[4/3]',
  square: 'aspect-square',
};

export interface DiscoveryOfferMediaProps {
  readonly offer: PublicDiscoveryOfferItemDTO;
  readonly testId: string;
  readonly aspect?: DiscoveryOfferAspectRatio;
  readonly className?: string;
  readonly imageClassName?: string;
  readonly imageAlt?: string;
  /** When true, render nothing if the offer has no image URL (cards only). */
  readonly hideWhenEmpty?: boolean;
}

function joinClasses(...parts: ReadonlyArray<string | undefined>): string {
  return parts.filter((part) => part !== undefined && part.length > 0).join(' ');
}

export function DiscoveryOfferMedia(props: DiscoveryOfferMediaProps): JSX.Element | null {
  const {
    offer,
    testId,
    aspect = '4/3',
    className,
    imageClassName,
    imageAlt = '',
    hideWhenEmpty = false,
  } = props;
  const { t } = useTranslation('customer');
  const [brokenImageUrl, setBrokenImageUrl] = useState<string | null>(null);
  const placeholderLabel = t('browse.discovery.detailImagePlaceholder');
  const displayImageUrl = resolveDiscoveryOfferDisplayImageUrl(offer);
  const showImage =
    displayImageUrl !== null &&
    displayImageUrl.length > 0 &&
    brokenImageUrl !== displayImageUrl &&
    !isBrokenCatalogImageUrl(displayImageUrl);

  if (hideWhenEmpty && (displayImageUrl === null || displayImageUrl.length === 0)) {
    return null;
  }

  return (
    <div
      className={joinClasses(
        'relative w-full overflow-hidden bg-[var(--color-surface-muted)]',
        ASPECT_CLASS[aspect],
        className,
      )}
      data-testid={testId}
      data-aspect={aspect}
    >
      {showImage ? (
        <img
          key={`${offer.productContext.productId}-${displayImageUrl}`}
          src={displayImageUrl}
          alt={imageAlt}
          className={joinClasses(
            'h-full w-full object-cover object-center',
            imageClassName,
          )}
          loading="lazy"
          decoding="async"
          draggable={false}
          onError={() => {
            if (displayImageUrl === null) {
              return;
            }
            setBrokenImageUrl(displayImageUrl);
            noteCatalogImageLoadFailure(displayImageUrl);
          }}
        />
      ) : (
        <div
          className="relative flex h-full w-full items-center justify-center overflow-hidden"
          data-testid={`${testId}-placeholder`}
        >
          <div
            className="absolute inset-0 opacity-40"
            style={{
              backgroundImage:
                'repeating-linear-gradient(135deg, var(--color-outline-variant) 0, var(--color-outline-variant) 1px, transparent 1px, transparent 10px)',
            }}
            aria-hidden="true"
          />
          <div className="relative flex max-w-[12rem] flex-col items-center gap-1.5 px-4 text-center">
            <CatalogImagePlaceholder
              label={placeholderLabel}
              className="[&_svg]:h-8 [&_svg]:w-8"
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: 'transparent',
                color: 'var(--color-on-surface-muted)',
              }}
            />
            <span className="text-xs leading-snug text-[var(--color-on-surface-muted)]">
              {placeholderLabel}
            </span>
          </div>
        </div>
      )}
    </div>
  );
}
